<template>
  <div class="column content-box">

    <report-filter-selector
      :show-agents-filter="false"
      :show-group-by-filter="true"
      :show-business-hours-switch="false"
      @filter-change="onFilterChange"
    />
    <bot-automation-metrics :filters="requestPayload" />
    <div class="report-bar">
      <woot-loading-state
        v-if="accountReport.isFetching[this.currentMetric]"
        :message="$t('REPORT.LOADING_CHART')"
      />
      <div v-else class="chart-container">
        <woot-stacked-vertical-bar
          v-if="accountReport.data[this.currentMetric]"
          :collection="collection"
        />
        <span v-else class="empty-state">
          {{ $t('REPORT.NO_ENOUGH_DATA') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import ReportFilterSelector from './components/FilterSelector';
import { GROUP_BY_FILTER } from './constants';
import reportMixin from 'dashboard/mixins/reportMixin';
import alertMixin from 'shared/mixins/alertMixin';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import BotAutomationMetrics from './components/BotAutomationMetrics';

const REPORTS_KEYS = {
  CONVERSATIONS: 'conversations_count',
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  BOT_AUTOMATION: 'message_agent_and_bot_count',
};

export default {
  name: 'BotAutomation',
  components: {
    ReportFilterSelector,
    BotAutomationMetrics
  },
  mixins: [reportMixin, alertMixin],
  data() {
    return {
      from: 0,
      to: 0,
      currentSelection: 0,
      groupBy: GROUP_BY_FILTER[1],
      businessHours: false,
    };
  },

  computed: {
    ...mapGetters({
      accountReport: 'getAccountReports',
    }),
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        user_ids: this.userIds,
        inbox_id: this.inbox,
        team_id: this.team,
        rating: this.rating,
      };
    },
    collection() {
      const labels = this.accountReport.data[this.currentMetric].map((element) => {
        if (this.groupBy?.period === GROUP_BY_FILTER[2].period) {
            let week_date = new Date(fromUnixTime(element.timestamp));
            const first_day = week_date.getDate() - week_date.getDay();
            const last_day = first_day + 6;

            const week_first_date = new Date(week_date.setDate(first_day));
            const week_last_date = new Date(week_date.setDate(last_day));

            return `${format(week_first_date, 'dd/MM/yy')} - ${format(
              week_last_date,
              'dd/MM/yy'
            )}`;
          }
          if (this.groupBy?.period === GROUP_BY_FILTER[3].period) {
            return format(fromUnixTime(element.timestamp), 'MMM-yyyy');
          }
          if (this.groupBy?.period === GROUP_BY_FILTER[4].period) {
            return format(fromUnixTime(element.timestamp), 'yyyy');
          }
          return format(fromUnixTime(element.timestamp), 'dd-MMM-yyyy');
      })

      let botValueArr = []
      let agentValueArr = []
      let botObjValue = {}
      let agentObjValue = {}
      let datasets = []

      this.accountReport.data[this.currentMetric].map((item) => {
        botValueArr.push(item.value[0])
        agentValueArr.push(item.value[1])
        botObjValue = {
          label: 'Bot',
          backgroundColor: '#44ce4b',
          timestamp : item.timestamp,
          data: botValueArr
        }

        agentObjValue = {
          label: 'Agent',
          backgroundColor: '#1f93ff',
          timestamp : item.timestamp,
          data: agentValueArr
        }

      })
      datasets = [...datasets, botObjValue, agentObjValue]

      return {
        labels,
        datasets
      }
    },
    metrics() {
      const reportKeys = ['BOT_AUTOMATION'];
      const infoText = {
        FIRST_RESPONSE_TIME: this.$t(
          `REPORT.METRICS.FIRST_RESPONSE_TIME.INFO_TEXT`
        ),
        RESOLUTION_TIME: this.$t(`REPORT.METRICS.RESOLUTION_TIME.INFO_TEXT`),
      };
      return reportKeys.map(key => ({
        NAME: this.$t(`REPORT.METRICS.${key}.NAME`),
        KEY: REPORTS_KEYS[key],
        DESC: this.$t(`REPORT.METRICS.${key}.DESC`),
        INFO_TEXT: infoText[key],
        TOOLTIP_TEXT: `REPORT.METRICS.${key}.TOOLTIP_TEXT`,
      }));
    },
    currentMetric() {
      return this.metrics[this.currentSelection].KEY;
    },
  },
  methods: {
    fetchAllData() {
      this.fetchChartData();
      this.fetchBotAutomationSummary();
    },
    fetchChartData() {
      try {
        this.$store.dispatch('fetchAccountReport', {
          metric: this.currentMetric,
          ...this.getRequestPayload(),
        });
      } catch {
        this.showAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
    fetchBotAutomationSummary() {
      try {
        this.$store.dispatch(
          'fetchBotAutomationSummary',
          this.getRequestPayload()
        );
      } catch {
        this.showAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED'));
      }
    },
    getRequestPayload() {
      const { from, to, groupBy, businessHours } = this;

      return {
        from,
        to,
        groupBy: groupBy?.period,
        businessHours,
      };
    },
    onFilterChange({ from, to, groupBy, businessHours }) {
      this.from = from;
      this.to = to;
      this.groupBy = groupBy;
      this.businessHours = businessHours;
      this.fetchAllData();

      this.$track(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: { from, to, groupBy, businessHours },
        reportType: 'conversations',
      });
    },
  },
};
</script>
